import { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { MapContainer, Marker, Popup, TileLayer, CircleMarker } from "react-leaflet";
import { NavLink } from "react-router-dom";
import FleetWebApi from "../Api/FleetWebApi";

const DEFAULT_START_DATE = new Date() - 12096e5; // now - 14 days
const DEFAULT_END_DATE = new Date();

function CompanionDeviceMap(props) {
  const startDate = props.startDate !== undefined ? props.startDate : DEFAULT_START_DATE;
  const endDate = props.endDate !== undefined ? props.endDate : DEFAULT_END_DATE;
  const [mapData, setMapData] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!props.focused) return;
    let isMounted = true;

    FleetWebApi.getCompanionDeviceMap(
      props.coreId,
      startDate,
      endDate,
      props.virtualId
    )
      .then((result) => {
        if (isMounted) {
          setMapData(result);
          setIsLoaded(true);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err);
          setIsLoaded(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [props.coreId, props.virtualId, startDate, endDate, props.focused]);

  const daysAnalyzed =
    startDate && endDate
      ? Math.round((new Date(endDate) - new Date(startDate)) / 86400000)
      : null;

  let mapContent;

  if (!props.focused) {
    mapContent = <div>Waiting for focus</div>;
  } else if (error) {
    mapContent = <div>Error: {error.message}</div>;
  } else if (!isLoaded) {
    mapContent = <CircularProgress color="secondary" />;
  } else if (!mapData || (!mapData.device && (!mapData.proxies || mapData.proxies.length === 0))) {
    mapContent = <div>No location data available</div>;
  } else {
    const devicePos =
      mapData.device != null
        ? [mapData.device.latitude, mapData.device.longitude]
        : null;
    const validProxies = (mapData.proxies || []).filter(
      (p) => p.latitude != null && p.longitude != null
    );

    // Apply a small circular offset to proxies that share the same coordinates
    // so they don't stack invisibly on top of each other.
    const OFFSET = 0.00015;
    const seen = {};
    const offsetProxies = validProxies.map((proxy) => {
      const key = `${parseFloat(proxy.latitude).toFixed(5)},${parseFloat(proxy.longitude).toFixed(5)}`;
      const count = seen[key] || 0;
      seen[key] = count + 1;
      if (count === 0) return { ...proxy, lat: proxy.latitude, lng: proxy.longitude };
      const angle = (count * (2 * Math.PI)) / 6;
      return {
        ...proxy,
        lat: parseFloat(proxy.latitude) + OFFSET * Math.sin(angle),
        lng: parseFloat(proxy.longitude) + OFFSET * Math.cos(angle),
      };
    });

    const firstProxyPos =
      offsetProxies.length > 0 ? [offsetProxies[0].lat, offsetProxies[0].lng] : null;
    const center = devicePos || firstProxyPos || [0, 0];

    mapContent = (
      <MapContainer center={center} zoom={15} style={{ height: "400px" }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        />
        {devicePos && (
          <CircleMarker center={devicePos} color="blue" radius={10}>
            <Popup>
              <strong>Device</strong>
              <br />
              <NavLink to={"/device/" + mapData.device._id} style={{ color: "#1976d2", textDecoration: "underline" }}>
                {mapData.device.serial_number || mapData.device._id}
              </NavLink>
            </Popup>
          </CircleMarker>
        )}
        {offsetProxies.map((proxy, i) => (
          <Marker key={i} position={[proxy.lat, proxy.lng]}>
            <Popup>
              <strong>Recent Proxy</strong>
              <br />
              <NavLink to={"/device/" + proxy._id} style={{ color: "#1976d2", textDecoration: "underline" }}>
                {proxy.serial_number || proxy._id}
              </NavLink>
              {proxy.message_count != null && (
                <>
                  <br />
                  <span style={{ fontSize: "0.85em", color: "#555" }}>
                    Device Logs{daysAnalyzed != null ? ` (${daysAnalyzed} days)` : ""}: {proxy.message_count}
                  </span>
                </>
              )}
              {proxy.last_seen && (
                <>
                  <br />
                  <span style={{ fontSize: "0.85em", color: "#555" }}>
                    Last Connected: {new Date(proxy.last_seen).toLocaleString()}
                  </span>
                </>
              )}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    );
  }

  return (
    <Container maxWidth="lg" className="container" style={{ paddingBottom: 0 }}>
      <Grid item>
        <h3>Device Location for {props.coreId}</h3>
        {mapContent}
      </Grid>
    </Container>
  );
}

export default CompanionDeviceMap;
